// src/features/stock/components/StockTable.js
import React, { useState } from 'react';
import { 
  Edit, 
  Trash2, 
  Eye, 
  MoreVertical, 
  Package, 
  AlertTriangle,
  Calendar,
  Archive
} from 'lucide-react';
import { format } from 'date-fns';

const StockTable = ({ 
  items, 
  onEdit, 
  onDelete, 
  onView, 
  onMovement,
  selectedItems,
  onSelectItem,
  onSelectAll 
}) => {
  const [expandedRow, setExpandedRow] = useState(null);

  // Calculate stock status based on your data structure
  const getStockStatus = (item) => {
    // You'll need to implement this based on your business logic
    // This is just an example - adjust according to your needs
    if (!item.is_active) return 'inactive';
    return 'active'; // Default status
  };

  // Render status badge from API or fallback
  const renderStatusBadge = (item) => {
    if (item.status_badge) {
      return <div dangerouslySetInnerHTML={{ __html: item.status_badge }} />;
    }
    
    // Fallback status badge if API doesn't provide one
    const status = getStockStatus(item);
    const statusConfig = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Check if stock is low based on your data
  const isLowStock = (item) => {
    // You need to have a current quantity field
    // This might come from a separate API or you need to calculate it
    const currentQuantity = item.current_stock || 0;
    return currentQuantity <= (item.reorder_level || 0);
  };

  // Check if expiry is soon - you'd need expiry_date in your data
  const isExpiringSoon = (item) => {
    // If you don't have expiry tracking yet, return false
    return false;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedItems.length === items.length && items.length > 0}
                  onChange={onSelectAll}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Item Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stock Info
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Manufacturer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item) => (
              <React.Fragment key={item.id}>
                <tr className={`hover:bg-gray-50 transition-colors ${expandedRow === item.id ? 'bg-blue-50' : ''}`}>
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => onSelectItem(item.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
                        <Package className="h-5 w-5 text-primary-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {item.display_name || item.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          Code: {item.code || 'N/A'}
                        </div>
                        {item.generic_name && (
                          <div className="text-xs text-gray-400">
                            Generic: {item.generic_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      {item.category.name} {/* You'd need category_name from joined data */}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {item.strength || 'N/A'} - {item.form || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        Unit: {item.unit || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                       Available: {item.total_available} | Min: {item.minimum_stock} | Max: {item.maximum_stock} | Reorder: {item.reorder_level}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{item.manufacturer || 'N/A'}</div>
                    {item.requires_prescription && (
                      <span className="text-xs text-amber-600">Prescription Required</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {renderStatusBadge(item)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onView(item)}
                        className="p-1 text-gray-500 hover:text-primary-600 transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      {/* <button
                        onClick={() => onMovement(item)}
                        className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                        title="Record Movement"
                      >
                        <Archive className="h-5 w-5" />
                      </button> */}
                      <button
                        onClick={() => onEdit(item)}
                        className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => onDelete(item)}
                        className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setExpandedRow(expandedRow === item.id ? null : item.id)}
                        className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRow === item.id && (
                  <tr className="bg-gray-50">
                    <td colSpan="7" className="px-6 py-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Storage Conditions</h4>
                          <p className="text-sm text-gray-600">{item.storage_conditions || 'Room temperature'}</p>
                          {item.metadata?.requires_cold_chain && (
                            <span className="text-xs text-blue-600">❄️ Requires cold chain</span>
                          )}
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Tracking Info</h4>
                          <p className="text-sm text-gray-600">
                            Batch tracking: {item.metadata?.batch_tracking_required ? 'Yes' : 'No'}
                          </p>
                          <p className="text-sm text-gray-600">
                            Expiry tracking: {item.metadata?.expiry_tracking_required ? 'Yes' : 'No'}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Settings</h4>
                          <p className="text-sm text-gray-600">
                            Auto-update: {item.settings?.auto_update_stock ? 'Yes' : 'No'}
                          </p>
                          <p className="text-sm text-gray-600">
                            Partial issue: {item.settings?.allow_partial_issue ? 'Yes' : 'No'}
                          </p>
                        </div>
                      </div>
                      {(item.description || item.instructions) && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          {item.description && (
                            <p className="text-sm text-gray-600 mb-2">
                              <span className="font-medium">Description:</span> {item.description}
                            </p>
                          )}
                          {item.instructions && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Instructions:</span> {item.instructions}
                            </p>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      
      {items.length === 0 && (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No stock items found</h3>
          <p className="text-gray-500">Get started by adding your first inventory item.</p>
        </div>
      )}
    </div>
  );
};

export default StockTable;